import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../services/adminService';
import { useModal } from '../context/ModalContext';

export default function ManageClinicsPage() {
  const navigate = useNavigate();
  const { showAlert, showConfirm } = useModal();
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingClinic, setEditingClinic] = useState(null);
  const [newClinic, setNewClinic] = useState({ name: '' });

  useEffect(() => {
    loadClinics();
  }, []);

  const loadClinics = async () => {
    try {
      setLoading(true);
      const data = await adminService.getAllClinics();
      setClinics(data);
    } catch (error) {
      console.error('Failed to load clinics:', error);
      showAlert('Failed to load clinics', 'Error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddClinic = async () => {
    if (!newClinic.name) {
      await showAlert('Please enter clinic name', 'Validation');
      return;
    }

    try {
      await adminService.createClinic(newClinic);
      setShowAddModal(false);
      setNewClinic({ name: '' });
      await loadClinics();
    } catch (error) {
      console.error('Failed to create clinic:', error);
      await showAlert('Failed to create clinic', 'Error');
    }
  };

  const handleEditClinic = (clinic) => {
    setEditingClinic({
      id: clinic.id,
      name: clinic.name
    });
    setShowEditModal(true);
  };

  const handleUpdateClinic = async () => {
    if (!editingClinic.name) {
      await showAlert('Please enter clinic name', 'Validation');
      return;
    }

    try {
      await adminService.updateClinic(editingClinic.id, { name: editingClinic.name });
      setShowEditModal(false);
      setEditingClinic(null);
      await loadClinics();
    } catch (error) {
      console.error('Failed to update clinic:', error);
      await showAlert('Failed to update clinic', 'Error');
    }
  };

  const handleDeleteClinic = async (clinicId, clinicName) => {
    const ok = await showConfirm(`Are you sure you want to delete clinic "${clinicName}"?`, { title: 'Delete Clinic', danger: true });
    if (!ok) return;

    try {
      await adminService.deleteClinic(clinicId);
      await loadClinics();
    } catch (error) {
      console.error('Failed to delete clinic:', error);
      await showAlert('Failed to delete clinic', 'Error');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa' }}>
      <header className="dashboard-header">
        <div className="dashboard-header-left">
          <button 
            onClick={() => navigate('/admin/users')}
            style={{ 
              background: 'none', 
              border: 'none', 
              cursor: 'pointer',
              padding: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#333'
            }}
          >
            <span></span>
            <span>Back to Users & Clinics</span>
          </button>
        </div>
        <h1>Manage Clinics</h1>
        <div className="dashboard-header-right">
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            + Add Clinic
          </button>
        </div>
      </header>

      <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>Loading...</div>
        ) : (
          <div style={{ background: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Clinic Name</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Created Date</th>
                  <th style={{ padding: '1rem', textAlign: 'right', fontWeight: '600' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clinics.map(clinic => (
                  <tr key={clinic.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '1rem', fontWeight: '500' }}>{clinic.name}</td>
                    <td style={{ padding: '1rem', color: '#666', fontSize: '0.875rem' }}>
                      {clinic.createdAt ? new Date(clinic.createdAt).toLocaleDateString() : '-'}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right' }}>
                      <button
                        onClick={() => handleEditClinic(clinic)}
                        style={{
                          padding: '0.5rem 1rem',
                          background: '#e3f2fd',
                          color: '#1976d2',
                          border: '1px solid #90caf9',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          marginRight: '0.5rem'
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteClinic(clinic.id, clinic.name)}
                        style={{
                          padding: '0.5rem 1rem',
                          background: '#fee',
                          color: '#dc3545',
                          border: '1px solid #f5c6cb',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.875rem'
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {clinics.length === 0 && (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>
                No clinics found. Click "Add Clinic" to create one.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Clinic Modal */}
      {showAddModal && (
        <div className="custom-modal-overlay">
          <div className="custom-modal" onClick={e => e.stopPropagation()}>
            <div className="custom-modal-header">
              <h3>Add New Clinic</h3>
              <button className="close-btn" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="custom-modal-body">
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Clinic Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={newClinic.name}
                  onChange={e => setNewClinic({ name: e.target.value })}
                  placeholder="Enter clinic name"
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleAddClinic}>
                  Add Clinic
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Clinic Modal */}
      {showEditModal && editingClinic && (
        <div className="custom-modal-overlay">
          <div className="custom-modal" onClick={e => e.stopPropagation()}>
            <div className="custom-modal-header">
              <h3>Edit Clinic</h3>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="custom-modal-body">
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Clinic Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={editingClinic.name}
                  onChange={e => setEditingClinic({ ...editingClinic, name: e.target.value })}
                  placeholder="Enter clinic name"
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                <button className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleUpdateClinic}>
                  Update Clinic
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
